import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { PostAPI } from "../../Components/Backend/PostAPI";
import { usePermiteAcesso } from "../../Components/Funcionais/Utils";
import { GetAPI } from "../../Components/Backend/GetAPI";
import GaugeChart from "react-gauge-chart";
import APIAxios from "../../Api/APIAxios";
import SessaoAuth from "../../Hooks/SessaoAuth";
import ProdutGeral from "./ProdutGeral";
import EstatisticaGeral from "./EstatisticaGeral";
import ProdutividadeDia from "./ProdutividadeDia";
import Avisos from "./Avisos";
import ProdutividadeHora from "./ProdutividadeHora";
import ProdutividadeSemana from "./ProdutividadeSemana";
import PageTitle from "../../components/Layout/PageTitle";

const URL_Produtividade = "/resumo/produtividade";
const URL_GRUPOS = "/grupos";

const DashboardGeral = () => {
  usePermiteAcesso(7);
  const [avisoErro, setAvisoErro] = useState("");
  const [avisoSucesso, setAvisoSucesso] = useState("");
  const [loading, setLoading] = useState(false);

  const { sessao } = SessaoAuth();

  const [computador, setComputador] = useState("*");
  const [usuarios, setUsuarios] = useState([]);
  const [horasDisp, setHorasDisp] = useState(0);
  const [horasProd, setHorasProd] = useState(0);
  const [minDisp, setMinDisp] = useState(0);
  const [minProd, setMinProd] = useState(0);
  const [grupos, setGrupos] = useState([]);

  const apiAxios = APIAxios();

  const loadProdutividade = async () => {
    setLoading(true);

    const response = await PostAPI(apiAxios, URL_Produtividade, {
      data_inicio: sessao.dataIni,
      data_fim: sessao.dataFim,
      computador,
    });

    setLoading(false);

    if (response.success) {
      setHorasDisp(
        response.data
          .reduce((sum, user) => sum + parseFloat(user.minTotal) / 60, 0)
          .toFixed(0)
      );
      setHorasProd(
        response.data
          .reduce((sum, user) => sum + parseFloat(user.minRegistrados) / 60, 0)
          .toFixed(0)
      );
      setMinDisp(
        response.data
          .reduce((sum, user) => sum + parseFloat(user.minTotal), 0)
          .toFixed(0)
      );
      setMinProd(
        response.data
          .reduce((sum, user) => sum + parseFloat(user.minRegistrados), 0)
          .toFixed(0)
      );
      setUsuarios(response.data);
    } else {
      setAvisoErro(
        response.errorMessage || "Erro ao carregar dados de produtividade."
      );
    }
  };

  const listarGrupos = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    const response = await GetAPI(apiAxios, URL_GRUPOS);
    setLoading(false);

    if (!response.success) {
      setGrupos(response.data);
      setAvisoErro(response.errorMessage);
    } else {
      setGrupos(response.data);
    }
  };

  const gruposOrdenados = () => {
    return [...(grupos || [])].sort((a, b) => {
      return a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" });
    });
  };

  useEffect(() => {
    loadProdutividade();
    listarGrupos();
  }, [sessao]);

  const calcularProdutividadeGrupo = (idGrupo) => {
    if (usuarios.length === 0) return 0;

    const usuariosDoGrupo = usuarios.filter((u) => u.idGrupo === idGrupo);

    if (usuariosDoGrupo.length === 0) return 0;

    const total = usuariosDoGrupo.reduce((sum, user) => sum + user.minTotal, 0);

    const registrado = usuariosDoGrupo.reduce(
      (sum, user) => sum + user.minRegistrados,
      0
    );
    return (registrado * 100) / total > 100
      ? 1
      : (registrado / total).toFixed(4);
  };

  return (
    <Box>
      <PageTitle
        caption="Indicadores Gerais"
        setAvisoErro={setAvisoErro}
        avisoErro={avisoErro}
        avisoSucesso={avisoSucesso}
        setAvisoSucesso={setAvisoSucesso}
        loading={loading}
        accentStyle
      />

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} sx={{ mt: { xs: 4, md: 0 } }}>
            <ProdutGeral horasDisp={minDisp} horasProd={minProd} />
          </Grid>
          <Grid item xs={12} md={4}>
            <EstatisticaGeral
              horasDisp={horasDisp}
              horasProd={horasProd}
              qtdColab={usuarios ? usuarios.length : 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Avisos />
          </Grid>
          <Grid item xs={12} md={3}>
            <ProdutividadeSemana />
          </Grid>
          <Grid item xs={12} md={5}>
            <ProdutividadeDia />
          </Grid>
          <Grid item xs={12} md={4}>
            <ProdutividadeHora />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3, mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box sx={{
            width: 4,
            height: 28,
            borderRadius: "4px",
            background: "linear-gradient(180deg, #1A3865, #647DEE)",
          }} />
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: "#1A3865",
            letterSpacing: "0.4px",
          }}>
            Indicadores de Grupos
          </Typography>
          <Divider sx={{ flex: 1, borderColor: "rgba(100, 125, 238, 0.25)" }} />
        </Box>

        <Grid container spacing={2}>
          {gruposOrdenados().map((grupo) => (
            <Grid
              key={grupo.id}
              item
              xs={12}
              md={3}
              container
              justifyContent="center"
            >
              <Card
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  background: "transparent",
                  boxShadow: "0 8px 28px rgba(26, 56, 101, 0.15), 0 2px 6px rgba(0,0,0,0.06)",
                  borderRadius: "16px",
                  border: "1px solid rgba(100, 125, 238, 0.2)",
                  overflow: "hidden",
                  position: "relative",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 32px rgba(26, 56, 101, 0.18), 0 4px 10px rgba(100,125,238,0.1)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0, left: 0, right: 0,
                    height: "4px",
                    background: "linear-gradient(90deg, #1A3865, #647DEE, #269FFB)",
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, pt: 3, pb: "16px !important" }} align="center">
                  <Typography variant="subtitle2" sx={{
                    fontWeight: 700,
                    color: "#1A3865",
                    letterSpacing: "0.4px",
                    mb: 0.5,
                  }}>
                    {grupo.nome}
                  </Typography>
                  <GaugeChart
                    align="center"
                    id={`gauge-grupo-${grupo.id}`}
                    nrOfLevels={3}
                    percent={calcularProdutividadeGrupo(grupo.id)}
                    arcsLength={[0.6, 0.2, 0.2]}
                    colors={["#E57373", "#FFD54F", "#81C784"]}
                    arcWidth={0.25}
                    arcPadding={0.02}
                    textColor={"#1A3865"}
                    needleColor={"#78909C"}
                    animate={false}
                    style={{ width: "100%", maxWidth: "180px" }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default DashboardGeral;
